import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { centralService } from '../../../services/centralService';
import { userService } from '../../../services/userService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { useFilters } from '../../../hooks/useFilters';
import { formatFinancialRequirement } from '../../../constants/projectConstants';
import { getProjectTypeLabel } from '../../../constants/centralConstants';
import { financialYearUtils, debounce, dateUtils, arrayUtils } from '../../../utils/helpers';
import ExportPDFButton from '../../common/ExportPDFButton';
import { exportCentralClearanceOverallPDF } from '../../../utils/pdfExport';

const CentralOverallTable = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();

  const [loading, setLoading] = useState(true);
  const [consolidatedData, setConsolidatedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});
  const [userCache, setUserCache] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUpdatedByColumn, setShowUpdatedByColumn] = useState(false);
  const [exporting, setExporting] = useState(false);

  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: '',
    projectType: '',
    scheme: ''          // ← new
  });

  const filterConfig = getFilterConfig('CENTRAL_OVERALL');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const getUserDisplayName = async (userId) => {
    if (!userId) return 'System';
    if (userCache[userId]) return userCache[userId];

    try {
      const userData = await userService.getUserById(userId);
      const displayName = userData ?
        userData.fullName || userData.email || 'Unknown User' : 'Unknown User';

      setUserCache(prev => ({ ...prev, [userId]: displayName }));
      return displayName;
    } catch (error) {
      console.error('Error fetching user data:', error);
      const fallbackName = 'Unknown User';
      setUserCache(prev => ({ ...prev, [userId]: fallbackName }));
      return fallbackName;
    }
  };

  const loadUserData = async (projects) => {
    const userIds = [...new Set(projects.map(p => p.updatedBy || p.createdBy).filter(Boolean))];
    if (userIds.length === 0) return;

    setLoadingUsers(true);
    try {
      await Promise.all(userIds.map(userId => getUserDisplayName(userId)));
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const formatLastUpdated = (updatedBy, updatedAt) => {
    const userId = updatedBy || 'system';
    const userDisplayName = userCache[userId] || (loadingUsers ? 'Loading...' : 'Unknown User');
    const timeAgo = updatedAt ? dateUtils.formatDateTime(updatedAt) : 'Unknown';

    return { user: userDisplayName, time: timeAgo };
  };

  const debouncedAutoSave = useCallback(
    debounce(async (projectId, remarks) => {
      try {
        setSaving(prev => ({ ...prev, [projectId]: true }));
        await centralService.updateProjectRemarks(projectId, remarks, user.uid);

        setConsolidatedData(prev => prev.map(project =>
          project.id === projectId
            ? { ...project, remarks, updatedBy: user.uid, updatedAt: new Date() }
            : project
        ));

        setSaving(prev => ({ ...prev, [projectId]: false }));
        if (!userCache[user.uid]) await getUserDisplayName(user.uid);
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [projectId]: false }));
      }
    }, 1000),
    [user.uid, userCache]
  );

  const applyFiltersAndSort = () => {
    let filtered = [...consolidatedData];

    if (filters.search && filters.search.trim()) {
      filtered = arrayUtils.filterBySearch(filtered, filters.search, ['projectName', 'scheme']);
    }

    if (filters.projectType) {
      filtered = filtered.filter(project => project.type === filters.projectType);
    }

    // ← new scheme filter
    if (filters.scheme) {
      filtered = filtered.filter(project => project.scheme === filters.scheme);
    }

    setFilteredData(filtered);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [projectsResult, installmentsResult, ucsResult] = await Promise.all([
        centralService.getAllCentralProjects({ financialYear: filters.financialYear }),
        centralService.getAllInstallments({ financialYear: filters.financialYear }),
        centralService.getAllUCs({ financialYear: filters.financialYear })
      ]);

      if (!projectsResult.success || !installmentsResult.success || !ucsResult.success) {
        throw new Error('Failed to load data');
      }

      const consolidated = consolidateProjectData(
        projectsResult.data,
        installmentsResult.data,
        ucsResult.data
      );

      setConsolidatedData(consolidated);
      await loadUserData(consolidated);
    } catch (error) {
      console.error('Error loading central data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const consolidateProjectData = (projects, installments, ucs) => {
    return projects.map(project => {
      const projectInstallments = installments.filter(inst => inst.projectId === project.id);
      const projectUCs = ucs.filter(uc => uc.projectId === project.id);

      const centralInstallments = projectInstallments
        .filter(inst => inst.releaseType === 'central_doner' || inst.releaseType === 'central_nec')
        .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate))
        .map(inst => ({
          installmentNumber: inst.installmentNumber,
          releaseDate: inst.releaseDate,
          amount: parseFloat(inst.amount) || 0
        }));

      const stateInstallments = projectInstallments.filter(inst => inst.releaseType === 'state');

      const totalCentralReleased = centralInstallments.reduce((sum, inst) => sum + inst.amount, 0);
      const totalStateReleased = stateInstallments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0);
      const totalUCFurnished = projectUCs.reduce((sum, uc) => sum + (parseFloat(uc.ucFurnished) || 0), 0);
      const totalUCDue = projectUCs.reduce((sum, uc) => sum + (parseFloat(uc.ucDue) || 0), 0);

      const centralShare = parseFloat(project.centralShare) || 0;
      const stateShare = parseFloat(project.stateShare) || 0;

      return {
        id: project.id,
        projectName: project.projectName,
        scheme: project.scheme,
        type: project.type,
        financialYear: project.financialYear,
        centralShare,
        stateShare,
        totalCost: centralShare + stateShare,
        centralInstallments,
        totalCentralReleased,
        balanceToBeReleased: Math.max(0, centralShare - totalCentralReleased),
        stateCleared: totalStateReleased,
        stateDue: Math.max(0, stateShare - totalStateReleased),
        ucFurnished: totalUCFurnished,
        ucDue: totalUCDue,
        unspentBalance: Math.max(0, totalCentralReleased - totalUCFurnished),
        workOrderNumber: project.workOrderNumber || '',
        workOrderIssueDate: project.workOrderIssueDate || null,
        scheduledCompletionDate: project.scheduledCompletionDate || null,
        remarks: project.remarks || '',
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        createdBy: project.createdBy,
        updatedBy: project.updatedBy,
        installments: projectInstallments,
        ucs: projectUCs
      };
    });
  };

  const handleRemarksChange = (projectId, remarks) => {
    setConsolidatedData(prev => prev.map(project =>
      project.id === projectId ? { ...project, remarks } : project
    ));
    debouncedAutoSave(projectId, remarks);
  };

  const handleExportPDF = () => {
    try {
      setExporting(true);
      exportCentralClearanceOverallPDF(filteredData, filters);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadData(); }, [filters.financialYear]);
  useEffect(() => { applyFiltersAndSort(); }, [consolidatedData, filters.search, filters.projectType, filters.scheme]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading central projects overview..." />
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .enhanced-input:focus {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .enhanced-input {
          transition: all 0.2s ease-in-out;
        }
        .installment-row {
          background-color: #f9fafb;
        }
        .total-row {
          background-color: #e5e7eb;
          font-weight: 600;
          border-top: 2px solid #d1d5db;
        }
        .project-separator {
          border-bottom: 3px solid #9ca3af;
        }
      `}</style>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>Overall Projects Status</h1>
          <p className="text-text-secondary text-sm">
            Comprehensive overview of all central projects with installments and UC status
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <ExportPDFButton
            onClick={handleExportPDF}
            loading={exporting}
            disabled={!filteredData || filteredData.length === 0}
          />

          <button
            onClick={() => setShowUpdatedByColumn(!showUpdatedByColumn)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              showUpdatedByColumn
                ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title={showUpdatedByColumn ? "Hide Updated By column" : "Show Updated By column"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm font-medium">
              {showUpdatedByColumn ? 'Hide' : 'Show'} Updated By
            </span>
          </button>

          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
            dropdownProps={{
              buttonProps: { size: 'md' },
              contentProps: { layout: 'grid' }
            }}
          />
        </div>
      </div>

      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4 mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredData.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={hasActiveFilters ? "No projects match your filters" : "No central projects found"}
              description={
                hasActiveFilters
                  ? "Try adjusting your search or filter criteria"
                  : `No central projects found for ${filters.financialYear}`
              }
              actionLabel="Clear Filters"
              onAction={clearFilters}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border-collapse">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                <tr>
                  <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 w-16">S.NO</th>
                  <th rowSpan="2" className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase border border-gray-300 min-w-48">PROJECT/SCHEME</th>
                  <th colSpan="3" className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-blue-50">
                    APPROVED COST<br /><span className="text-gray-500 normal-case font-normal">(Rs in lakh)</span>
                  </th>
                  <th colSpan="3" className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-green-50">
                    AMT RELEASED BY {filters.projectType ? getProjectTypeLabel(filters.projectType).toUpperCase() : 'CENTRAL'}<br />
                    <span className="text-gray-500 normal-case font-normal">(Rs in lakh)</span>
                  </th>
                  <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-orange-50 w-24">
                    BAL AMT TO BE RELEASED<br /><span className="text-gray-500 normal-case font-normal">(Rs in lakh)</span>
                  </th>
                  <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-purple-50">
                    STATUS OF STATE'S SHARE<br /><span className="text-gray-500 normal-case font-normal">(Rs in lakh)</span>
                  </th>
                  <th colSpan="3" className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-yellow-50">
                    UC STATUS<br /><span className="text-gray-500 normal-case font-normal">(Rs in lakh)</span>
                  </th>
                  <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 w-40">
                    REMARKS/% WORK DONE
                  </th>
                  {showUpdatedByColumn && (
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 w-32">UPDATED BY</th>
                  )}
                </tr>
                <tr>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-blue-50 w-24">CENTRAL</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-blue-50 w-24">STATE</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-blue-50 w-24">TOTAL</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-green-50 w-24">INSTALLMENT</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-green-50 w-28">DATE</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-green-50 w-24">AMT</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-purple-50 w-24">CLEARED</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-purple-50 w-24">DUE</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-yellow-50 w-24">FURNISHED</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-yellow-50 w-24">DUE</th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border border-gray-300 bg-yellow-50 w-28">UNSPENT BALANCE</th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {filteredData.map((project, projectIndex) => {
                  const isProjectSaving = saving[project.id];
                  const lastUpdated = formatLastUpdated(project.updatedBy || project.createdBy, project.updatedAt || project.createdAt);
                  const hasInstallments = project.centralInstallments?.length > 0;
                  const totalRows = hasInstallments ? project.centralInstallments.length + 1 : 1;

                  return (
                    <React.Fragment key={project.id}>
                      {hasInstallments ? (
                        <>
                          {project.centralInstallments.map((inst, instIndex) => (
                            <tr key={`${project.id}-inst-${instIndex}`} className={instIndex > 0 ? 'installment-row' : ''}>
                              {instIndex === 0 && (
                                <>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center border border-gray-300 align-top">
                                    <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-xs font-bold text-blue-700">
                                      {projectIndex + 1}
                                    </div>
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 border border-gray-300 align-top">
                                    <div className="text-xs text-gray-900 font-bold mb-1">{project.projectName}</div>
                                    <div className="text-xs text-gray-600 mb-1">{project.scheme} • {getProjectTypeLabel(project.type)}</div>
                                    {project.workOrderNumber && (
                                      <div className="text-xs text-gray-500">WO: {project.workOrderNumber}</div>
                                    )}
                                    {isProjectSaving && (
                                      <div className="flex items-center mt-2">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                        <span className="ml-1 text-xs text-blue-600">Saving...</span>
                                      </div>
                                    )}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-blue-50 align-top">
                                    {formatFinancialRequirement(project.centralShare)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-blue-50 align-top">
                                    {formatFinancialRequirement(project.stateShare)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-bold border border-gray-300 bg-blue-50 align-top">
                                    {formatFinancialRequirement(project.totalCost)}
                                  </td>
                                </>
                              )}

                              <td className="px-3 py-2 text-center text-xs border border-gray-300 bg-green-50">
                                {inst.installmentNumber ? `${inst.installmentNumber}${['st', 'nd', 'rd'][inst.installmentNumber - 1] || 'th'}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-center text-xs border border-gray-300 bg-green-50">
                                {inst.releaseDate ? dateUtils.formatDate(inst.releaseDate) : '—'}
                              </td>
                              <td className="px-3 py-2 text-center text-xs font-medium border border-gray-300 bg-green-50">
                                {formatFinancialRequirement(inst.amount)}
                              </td>

                              {instIndex === 0 && (
                                <>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-bold border border-gray-300 bg-orange-50 align-top">
                                    {formatFinancialRequirement(project.balanceToBeReleased)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-purple-50 align-top">
                                    {formatFinancialRequirement(project.stateCleared)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-purple-50 align-top">
                                    {formatFinancialRequirement(project.stateDue)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50 align-top">
                                    {formatFinancialRequirement(project.ucFurnished)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50 align-top">
                                    {formatFinancialRequirement(project.ucDue)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50 align-top">
                                    {formatFinancialRequirement(project.unspentBalance)}
                                  </td>
                                  <td rowSpan={totalRows} className="px-3 py-3 border border-gray-300 align-top">
                                    <textarea
                                      value={project.remarks}
                                      onChange={(e) => handleRemarksChange(project.id, e.target.value)}
                                      className="enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                      placeholder="Enter remarks..."
                                      rows="3"
                                    />
                                  </td>
                                  {showUpdatedByColumn && (
                                    <td rowSpan={totalRows} className="px-3 py-3 text-center border border-gray-300 align-top">
                                      <div className="text-xs">
                                        <div className="font-semibold text-gray-800 mb-1">{lastUpdated.user}</div>
                                        <div className="text-gray-500">{lastUpdated.time}</div>
                                      </div>
                                    </td>
                                  )}
                                </>
                              )}
                            </tr>
                          ))}

                          <tr className="total-row project-separator">
                            <td colSpan="2" className="px-3 py-2 text-center text-xs font-bold border border-gray-300 bg-green-50">
                              Total
                            </td>
                            <td className="px-3 py-2 text-center text-xs font-bold border border-gray-300 bg-green-50">
                              {formatFinancialRequirement(project.totalCentralReleased)}
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr className="project-separator">
                          <td className="px-3 py-3 text-center border border-gray-300">
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-xs font-bold text-blue-700">
                              {projectIndex + 1}
                            </div>
                          </td>
                          <td className="px-3 py-3 border border-gray-300">
                            <div className="text-xs text-gray-900 font-bold mb-1">{project.projectName}</div>
                            <div className="text-xs text-gray-600 mb-1">{project.scheme} • {getProjectTypeLabel(project.type)}</div>
                            {project.workOrderNumber && (
                              <div className="text-xs text-gray-500">WO: {project.workOrderNumber}</div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-blue-50">
                            {formatFinancialRequirement(project.centralShare)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-blue-50">
                            {formatFinancialRequirement(project.stateShare)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-bold border border-gray-300 bg-blue-50">
                            {formatFinancialRequirement(project.totalCost)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs border border-gray-300 bg-green-50">—</td>
                          <td className="px-3 py-3 text-center text-xs border border-gray-300 bg-green-50">—</td>
                          <td className="px-3 py-3 text-center text-xs border border-gray-300 bg-green-50">₹0.00</td>
                          <td className="px-3 py-3 text-center text-xs font-bold border border-gray-300 bg-orange-50">
                            {formatFinancialRequirement(project.balanceToBeReleased)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-purple-50">
                            {formatFinancialRequirement(project.stateCleared)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-purple-50">
                            {formatFinancialRequirement(project.stateDue)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50">
                            {formatFinancialRequirement(project.ucFurnished)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50">
                            {formatFinancialRequirement(project.ucDue)}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border border-gray-300 bg-yellow-50">
                            {formatFinancialRequirement(project.unspentBalance)}
                          </td>
                          <td className="px-3 py-3 border border-gray-300">
                            <textarea
                              value={project.remarks}
                              onChange={(e) => handleRemarksChange(project.id, e.target.value)}
                              className="enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              placeholder="Enter remarks..."
                              rows="3"
                            />
                          </td>
                          {showUpdatedByColumn && (
                            <td className="px-3 py-3 text-center border border-gray-300">
                              <div className="text-xs">
                                <div className="font-semibold text-gray-800 mb-1">{lastUpdated.user}</div>
                                <div className="text-gray-500">{lastUpdated.time}</div>
                              </div>
                            </td>
                          )}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CentralOverallTable